import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Plus, X } from "lucide-react";
import { sampleTemplateData } from "../data/mockData";
import { tagsAPI, referenceDataAPI } from "../services/api";
import EditTemplateConfirmationModal from "../Modal/EditTemplateConfirmationModal";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 500px;
  height: 680px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #E5E7EB;
  position: relative;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  flex: 1;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  padding: 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  right: 0;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const ContentSection = styled.div`
  padding: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagContainer = styled.div`
  position: relative;
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const AddTagButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  color: #374151;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00B5B8;
  color: #00B5B8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00B5B8;
  cursor: pointer;
  color: #00B5B8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const GenderSection = styled.div`
  margin-bottom: 1rem;
`;

const GenderAgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
`;

const GenderColumn = styled.div`
  flex: 1;
`;

const AgeColumn = styled.div`
  flex: 1;
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RadioInput = styled.input`
  width: 0.9rem;
  height: 0.9rem;
  accent-color: #1D87F0;

  &:checked {
    background-color: #1D87F0;
    border-color: #1D87F0;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
`;

const AgeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
`;

const AgeInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AgeInput = styled.input`
  width: 70px;
  padding: 0.4rem 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  text-align: center;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #1D87F0;
    box-shadow: 0 0 0 3px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const AgeSeparator = styled.span`
  color: #6B7280;
  font-size: 0.8rem;
  font-weight: 500;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const MultiSelectWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  height: fit-content;

  &:hover {
    background-color: #0891B2;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #E5E7EB;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }
`;

const ParameterOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const Checkbox = styled.input`
  width: 0.8rem;
  height: 0.8rem;
  accent-color: #00B5B8;
`;

const TableContainer = styled.div`
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
`;

const TableCell = styled.td`
  padding: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ParameterInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const ParameterInput = styled.input`
  width: 65px;
  padding: 0.3rem 0.4rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  text-align: center;

  &::placeholder {
    color: #9CA3AF;
    font-size: 0.7rem;
  }

  &:focus {
    outline: none;
    border-color: #1D87F0;
    box-shadow: 0 0 0 2px rgba(29, 135, 240, 0.1);
  }

  /* ซ่อน spinner buttons */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const ParameterSeparator = styled.span`
  color: #6B7280;
  font-size: 0.75rem;
  font-weight: 500;
`;

const TableRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TableDualRangeContainer = styled.div`
  width: 100px;
  position: relative;
  height: 3px;
`;

const TableRangeTrack = styled.div`
  width: 100%;
  height: 3px;
  background: #E5E7EB;
  border-radius: 1.5px;
  position: relative;
`;

const TableRangeProgress = styled.div`
  height: 100%;
  background: #1D87F0;
  border-radius: 1.5px;
  position: absolute;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
`;

const TableRangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 3px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    z-index: 2;
    margin-top: -3.5px;
    transform: translateY(-0.5px);
  }

  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -3.5px;
  }
`;

const TableRangeDisplay = styled.div`
  font-size: 0.75rem;
  color: #374151;
  min-width: 70px;
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
`;

const NormalRangeBadge = styled.span`
  background: transparent;
  color: #374151;
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: normal;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const SaveButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #0891B2;
  }
`;

const EditTemplateModal = ({ isOpen, onClose, onSave, templateData = null }) => {
  const [templateName, setTemplateName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedGender, setSelectedGender] = useState("male");
  const [ageRange, setAgeRange] = useState([25, 65]);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [tempSelectedParameters, setTempSelectedParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [parameterRanges, setParameterRanges] = useState({});
  const [parameterRadioValues, setParameterRadioValues] = useState({}); // สำหรับเก็บค่า radio (ไม่มี/มี)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allParameters, setAllParameters] = useState([]);

  // ดึงข้อมูล tags และ parameters จาก API เมื่อเปิด modal
  useEffect(() => {
    if (!isOpen) return;

    const loadTags = async () => {
      try {
        // ดึง tags ที่เคยถูกใช้ใน templates (จาก template_tags)
        const response = await tagsAPI.getUsedTags();
        if (response.status === 'success' && response.data) {
          setAvailableTags(response.data);
          console.log('✅ โหลด used tags สำเร็จ:', response.data.length, 'รายการ');
        }
      } catch (error) {
        console.error('✗ Error loading tags:', error);
        setAvailableTags([]);
      }
    };

    const loadParameters = async () => {
      try {
        // ใช้ getParametersList แทน getAllParameters เพราะจัดรูปแบบข้อมูลให้แล้ว
        const response = await referenceDataAPI.getParametersList();
        if (response.status === 'success' && response.data) {
          // แปลงข้อมูลจาก API ให้ตรงกับรูปแบบที่ใช้
          const formattedParams = response.data.map(param => ({
            name: param.name,
            unit: param.unit || '',
            normalRange: param.normalRange || [0, 1000],
            normalValue: param.normalValue || '-',
            defaultRange: param.normalRange || [0, 100]
          }));
          setAllParameters(formattedParams);
          console.log('✅ โหลด parameters สำเร็จ:', formattedParams.length, 'รายการ');
          console.log('Sample parameter:', formattedParams[0]); // ดูตัวอย่าง
        }
      } catch (error) {
        console.error('✗ Error loading parameters:', error);
        setAllParameters([]);
      }
    };

    loadTags();
    loadParameters();
  }, [isOpen]);

  // 🔥 โหลดพารามิเตอร์ใหม่เมื่อเพศหรืออายุเปลี่ยน
  useEffect(() => {
    if (!isOpen) return;

    const loadParametersForGenderAge = async () => {
      try {
        // แปลงเพศจาก 'male'/'female'/'both' เป็น 'M'/'F'/null
        let gender = null;
        if (selectedGender === 'male') gender = 'M';
        else if (selectedGender === 'female') gender = 'F';

        // ใช้อายุกลางของ range
        const avgAge = (ageRange[0] + ageRange[1]) / 2;

        const response = await referenceDataAPI.getParametersList({
          gender: gender,
          age: avgAge
        });

        if (response.status === 'success' && response.data) {
          const formattedParams = response.data.map(param => ({
            name: param.name,
            unit: param.unit || '',
            normalRange: param.normalRange || [0, 1000],
            normalValue: param.normalValue || '-',
            defaultRange: param.normalRange || [0, 100]
          }));
          setAllParameters(formattedParams);
          console.log('✅ อัปเดตพารามิเตอร์ตามเพศ/อายุ:', formattedParams.length, 'รายการ');
        }
      } catch (error) {
        console.error('✗ Error loading parameters for gender/age:', error);
      }
    };

    loadParametersForGenderAge();
  }, [selectedGender, ageRange, isOpen]);

  // หมายเหตุ: ไม่ต้องอัปเดต normalValue เมื่อ allParameters เปลี่ยน
  // เพราะค่าปกติมาจากหน้าหลักแล้ว (จาก backend ที่คำนวณตามเพศ/อายุของ template)

  // โหลดข้อมูลเมื่อเปิด modal
  useEffect(() => {
    if (isOpen && templateData) {
      const dataToLoad = templateData || sampleTemplateData;
      setTemplateName(dataToLoad.templateName);
      setCreatedBy(dataToLoad.createdBy);
      setTags([...dataToLoad.tags]);
      setSelectedGender(dataToLoad.gender);

      // แปลง ageRange เป็น array ของตัวเลขหรือค่าว่าง
      const loadedAgeRange = Array.isArray(dataToLoad.ageRange)
        ? [dataToLoad.ageRange[0] ?? '', dataToLoad.ageRange[1] ?? '']
        : ['', ''];
      setAgeRange(loadedAgeRange);

      // โหลด parameters ก่อน (ยังไม่ merge)
      setSelectedParameters(dataToLoad.selectedParameters);
      setParameterRanges({ ...dataToLoad.parameterRanges });
    }
  }, [isOpen, templateData]);

  // Merge normalValue จาก allParameters หลังจากที่ allParameters โหลดเสร็จ
  useEffect(() => {
    if (selectedParameters.length > 0 && allParameters.length > 0) {
      // เช็คว่ามี parameter ที่ normalValue เป็น null หรือไม่
      const needsMerge = selectedParameters.some(p => !p.normalValue || p.normalValue === null);

      if (needsMerge) {
        const mergedParameters = selectedParameters.map(param => {
          const fullParam = allParameters.find(p => p.name === param.name);

          // ใช้ค่าจาก fullParam (API) เพราะมี normalValue จาก medium.json
          return {
            ...param,
            normalValue: fullParam?.normalValue || param.normalValue || '-',
            normalRange: fullParam?.normalRange || param.normalRange || [0, 100],
            unit: fullParam?.unit || param.unit || ''
          };
        });

        setSelectedParameters(mergedParameters);
        console.log('✅ Merged normalValue จาก API สำเร็จ');
      }
    }
  }, [allParameters, selectedParameters.length]);

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleMinAgeChange = (e) => {
    let value = e.target.value;
    // อนุญาตให้พิมพ์ค่าว่าง หรือทศนิยม
    if (value === '' || value === '.') {
      setAgeRange([value, ageRange[1]]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // จำกัดทศนิยมไม่เกิน 3 ตำแหน่ง
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([roundedValue, ageRange[1]]); // ไม่บังคับให้ค่าต้องเรียงกัน
    }
  };

  const handleMaxAgeChange = (e) => {
    let value = e.target.value;
    // อนุญาตให้พิมพ์ค่าว่าง หรือทศนิยม
    if (value === '' || value === '.') {
      setAgeRange([ageRange[0], value]);
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // จำกัดทศนิยมไม่เกิน 3 ตำแหน่ง
      const roundedValue = Math.round(numValue * 1000) / 1000;
      setAgeRange([ageRange[0], roundedValue]); // ไม่บังคับให้ค่าต้องเรียงกัน
    }
  };

  const filteredParameters = allParameters.filter(param => {
    try {
      // ตรวจสอบว่า param มีครบถ้วน
      if (!param || !param.name) {
        return false;
      }

      // แปลง param.name เป็น string ก่อนเสมอ
      const paramName = String(param.name);
      const search = String(searchTerm);

      return paramName.toLowerCase().includes(search.toLowerCase());
    } catch (error) {
      console.error('Error filtering parameter:', param, error);
      return false;
    }
  });

  const handleParameterToggle = (parameter) => {
    const isSelected = tempSelectedParameters.some(p => p.name === parameter.name);
    if (isSelected) {
      setTempSelectedParameters(tempSelectedParameters.filter(p => p.name !== parameter.name));
    } else {
      setTempSelectedParameters([...tempSelectedParameters, parameter]);
    }
  };

  // ฟังก์ชันตรวจสอบว่าพารามิเตอร์เป็นชนิด radio (ไม่มี/มี) หรือไม่
  const isRadioParameter = (parameter) => {
    const radioParams = [
      'Protein', 'Glucose', 'Ketones', 'Blood', 'Leukocytes', 'Nitrite', 'Bilirubin', 'Urobilinogen',
      'Color', 'Clarity',
      'Color (Stool)', 'Appearance (Stool)', 'WBC (Stool)', 'RBC (Stool)', 'Parasite',
      'RBC Index'
    ];
    return radioParams.includes(parameter.name);
  };

  // ฟังก์ชันเพื่อดึงตัวเลือกสำหรับ radio button แต่ละพารามิเตอร์
  const getRadioOptions = (parameterName) => {
    const optionsMap = {
      'Protein': ['ไม่มี', 'มี'],
      'Glucose': ['ไม่มี', 'มี'],
      'Ketones': ['ไม่มี', 'มี'],
      'Blood': ['ไม่มี', 'มี'],
      'Leukocytes': ['ไม่มี', 'มี'],
      'Nitrite': ['ไม่มี', 'มี'],
      'Bilirubin': ['ไม่มี', 'มี'],
      'Urobilinogen': ['ไม่มี', 'มี'],
      'Color': ['เหลืองอ่อน', 'ผิดปกติ'],
      'Clarity': ['Clear', 'ผิดปกติ'],
      'Color (Stool)': ['xxx'],
      'Appearance (Stool)': ['xxx'],
      'WBC (Stool)': ['Not found', 'Found'],
      'RBC (Stool)': ['Not found', 'Found'],
      'Parasite': ['Not found', 'Found'],
      'RBC Index': ['xxx'],
      'eGFR': ['xxx']
    };
    return optionsMap[parameterName] || ['ไม่มี', 'มี'];
  };

  const handleSelectParameters = () => {
    setSelectedParameters([...tempSelectedParameters]);

    const newRanges = { ...parameterRanges };
    const newRadioValues = { ...parameterRadioValues };

    Object.keys(newRanges).forEach(paramName => {
      if (!tempSelectedParameters.some(p => p.name === paramName)) {
        delete newRanges[paramName];
      }
    });

    Object.keys(newRadioValues).forEach(paramName => {
      if (!tempSelectedParameters.some(p => p.name === paramName)) {
        delete newRadioValues[paramName];
      }
    });

    tempSelectedParameters.forEach(parameter => {
      if (isRadioParameter(parameter)) {
        // ถ้าเป็น radio parameter ให้ตั้งค่าเริ่มต้นเป็นตัวเลือกแรก
        if (!newRadioValues[parameter.name]) {
          const options = getRadioOptions(parameter.name);
          newRadioValues[parameter.name] = options[0];
        }
      } else {
        // ถ้าเป็น range parameter ให้ใช้ defaultRange เป็นค่าเริ่มต้น
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = parameter.defaultRange;
        }
      }
    });

    setParameterRanges(newRanges);
    setParameterRadioValues(newRadioValues);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleDropdownOpen = () => {
    setTempSelectedParameters([...selectedParameters]);
    setIsDropdownOpen(true);
  };

  // ฟังก์ชันสร้าง placeholder จาก normalValue
  const getPlaceholderFromNormalValue = (normalValue) => {
    if (!normalValue) return ['', ''];

    const trimmed = normalValue.trim();

    // กรณี >50 → placeholder: "0-50"
    if (trimmed.startsWith('>')) {
      const val = trimmed.substring(1);
      return ['0', val];
    }
    // กรณี <130 → placeholder: "0-130"
    else if (trimmed.startsWith('<')) {
      const val = trimmed.substring(1);
      return ['0', val];
    }
    // กรณี 3.5-7.2 → placeholder: "3.5" และ "7.2"
    else if (trimmed.includes('-')) {
      const parts = trimmed.split('-');
      return parts.length === 2 ? [parts[0], parts[1]] : ['', ''];
    }
    // กรณีเป็นตัวเลขเดียว
    else {
      return [trimmed, trimmed];
    }
  };

  const handleParameterRangeChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || ['', ''];

    // อนุญาตให้พิมพ์ค่าว่าง
    if (value === '' || value === '.') {
      const newRange = isMin ? [value, currentRange[1]] : [currentRange[0], value];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange
      });
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const newRange = isMin ? [numValue, currentRange[1]] : [currentRange[0], numValue];
      setParameterRanges({
        ...parameterRanges,
        [paramName]: newRange
      });
    }
  };

  const handleRadioChange = (paramName, value) => {
    setParameterRadioValues({
      ...parameterRadioValues,
      [paramName]: value
    });
  };

  const handleSave = () => {
    // แสดง confirmation modal
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    const updatedTemplate = {
      templateName,
      createdBy,
      tags,
      gender: selectedGender,
      ageRange,
      selectedParameters,
      parameterRanges,
      parameterRadioValues
    };
    onSave(updatedTemplate);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>แก้ไข Template</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentSection>
            <FormGroup>
              <Label>ชื่อ Template</Label>
              <Input
                type="text"
                placeholder="ชื่อ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ชื่อผู้สร้าง</Label>
              <Input
                type="text"
                placeholder="ชื่อผู้สร้าง"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ป้ายกำกับ (Tag)</Label>
              <TagContainer>
                <TagInputContainer>
                  <TagInput
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addTag();
                      }
                    }}
                  />
                  <AddTagButton onClick={addTag}>เพิ่ม</AddTagButton>
                </TagInputContainer>

                <TagList>
                  {tags.map((tag, index) => (
                    <Tag key={index}>
                      <span>{tag}</span>
                      <TagRemoveButton onClick={() => removeTag(tag)}>
                        <X size={8} />
                      </TagRemoveButton>
                    </Tag>
                  ))}
                </TagList>
              </TagContainer>
            </FormGroup>

            <GenderSection>
              <GenderAgeRow>
                <GenderColumn>
                  <Label>สำหรับเพศ</Label>
                  <GenderOptions>
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={selectedGender === "male"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="male">ชาย</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={selectedGender === "female"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="female">หญิง</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="both"
                        name="gender"
                        value="both"
                        checked={selectedGender === "both"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="both">ทุกเพศ</RadioLabel>
                    </RadioContainer>
                  </GenderOptions>
                </GenderColumn>

                <AgeColumn>
                  <Label>ช่วงอายุ (ปี)</Label>
                  <AgeRangeContainer>
                    <AgeInputWrapper>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="0.000"
                        value={ageRange[0]}
                        onChange={handleMinAgeChange}
                      />
                      <AgeSeparator>-</AgeSeparator>
                      <AgeInput
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="999"
                        value={ageRange[1]}
                        onChange={handleMaxAgeChange}
                      />
                    </AgeInputWrapper>
                  </AgeRangeContainer>
                </AgeColumn>
              </GenderAgeRow>
            </GenderSection>

            <FormGroup>
              <Label>เลือกพาราเมิเตอร์</Label>
              <MultiSelectContainer>
                <MultiSelectWrapper>
                  <MultiSelectButton
                    type="button"
                    onClick={handleDropdownOpen}
                  >
                    <span>
                      {selectedParameters.length === 0
                        ? 'เลือกพาราเมิเตอร์'
                        : `${selectedParameters.length} selected`}
                    </span>
                    <span>{isDropdownOpen ? '▲' : '▼'}</span>
                  </MultiSelectButton>

                  {isDropdownOpen && (
                    <DropdownContent>
                      <SearchInput
                        type="text"
                        placeholder="ค้นหาพาราเมิเตอร์..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {filteredParameters.map((parameter, index) => (
                        <ParameterOption key={index} onClick={() => handleParameterToggle(parameter)}>
                          <Checkbox
                            type="checkbox"
                            checked={tempSelectedParameters.some(p => p.name === parameter.name)}
                            onChange={() => {}}
                          />
                          <span>{parameter.name} ({parameter.unit})</span>
                        </ParameterOption>
                      ))}
                    </DropdownContent>
                  )}
                </MultiSelectWrapper>

                <SelectButton onClick={handleSelectParameters}>
                  เลือก
                </SelectButton>
              </MultiSelectContainer>
            </FormGroup>

            <TableContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>พาราเมิเตอร์</TableHeaderCell>
                    <TableHeaderCell>ค่าปกติ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่กำหนด</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {selectedParameters.length > 0 ? (
                    selectedParameters.map((param, index) => {
                      const isRadio = isRadioParameter(param);

                      if (isRadio) {
                        // แสดง radio button สำหรับพารามิเตอร์ที่เป็น text-based
                        const options = getRadioOptions(param.name);
                        const radioValue = parameterRadioValues[param.name] || options[0];

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>{param.normalValue}</TableCell>
                            <TableCell>
                              <RadioOptionsContainer>
                                {options.map((option, optionIndex) => (
                                  <RadioContainer key={optionIndex}>
                                    <RadioInput
                                      type="radio"
                                      id={`${param.name}-${optionIndex}`}
                                      name={param.name}
                                      value={option}
                                      checked={radioValue === option}
                                      onChange={(e) => handleRadioChange(param.name, e.target.value)}
                                    />
                                    <RadioLabel htmlFor={`${param.name}-${optionIndex}`}>{option}</RadioLabel>
                                  </RadioContainer>
                                ))}
                              </RadioOptionsContainer>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // แสดง input fields สำหรับพารามิเตอร์ปกติ
                        const range = parameterRanges[param.name] || ['', ''];
                        const [minPlaceholder, maxPlaceholder] = getPlaceholderFromNormalValue(param.normalValue);

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>
                              <NormalRangeBadge>{param.normalValue}</NormalRangeBadge>
                            </TableCell>
                            <TableCell>
                              <ParameterInputWrapper>
                                <ParameterInput
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder={minPlaceholder}
                                  value={range[0]}
                                  onChange={(e) => handleParameterRangeChange(param.name, true, e.target.value)}
                                />
                                <ParameterSeparator>-</ParameterSeparator>
                                <ParameterInput
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder={maxPlaceholder}
                                  value={range[1]}
                                  onChange={(e) => handleParameterRangeChange(param.name, false, e.target.value)}
                                />
                              </ParameterInputWrapper>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    })
                  ) : (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </ContentSection>

          <ActionSection>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SaveButton onClick={handleSave}>ยืนยัน</SaveButton>
          </ActionSection>
        </ModalBody>
      </ModalContainer>

      <EditTemplateConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
      />
    </ModalOverlay>
  );
};

export default EditTemplateModal;